// employeeApiService.js
import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const baseUrl = 'https://api.innoworx.ai/Stage';

/**
 * Get authentication token from AWS Amplify
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) return tokens.idToken.toString();
    console.error('No ID Token found. User may not be logged in.');
    return null;
  } catch (error) {
    console.error('Error fetching auth session:', error);
    return null;
  }
};

/**
 * Get tenant info
 */
export const getTenantInfo = async () => {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required.');

  const res = await fetch(`${baseUrl}/tenant`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  return await res.json();
};

/**
 * Fetch roles by business vertical
 */
export const fetchRolesByVertical = async (vertical) => {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required.');

  const res = await fetch(`${baseUrl}/user/roles/by-vertical?business_vertical=${vertical}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  const data = await res.json();
  return data.roles ?? [];
};

/**
 * Get users by role
 */
export const getUsersByRole = async (role) => {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required.');

  const res = await fetch(`${baseUrl}/user/getall/by-spec-role?role=${role}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  return await res.json();
};

export const getAllUsers = async () => {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required.');

  const res = await fetch(`${baseUrl}/user/getall`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  return await res.json();
};


/**
 * Get profile photo
 */
export const getProfilePhoto = async (userId) => {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required.');

  const res = await fetch(`${baseUrl}/user/profile-picture?user_id=${userId}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  const data = await res.json();
  return data.profile_image_url;
};

/**
 * Delete user
 */
export const deleteUser = async (userId) => {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required.');

  // Clean userId just in case
  const cleanUserId = String(userId).trim();

  try {
    const res = await fetch(`${baseUrl}/user/delete?employee_id=${encodeURIComponent(cleanUserId)}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        // Remove Content-Type for DELETE without body
        // 'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      // Try reading json error message if available
      let errorMessage = `${res.status}: ${res.statusText}`;
      let errorData = {};
      try {
        errorData = await res.json();
        if (errorData.message) errorMessage += ` - ${errorData.message}`;
      } catch (_) {
        // ignore JSON parse errors
      }
      const error = new Error(`HTTP ${errorMessage}`);
      error.status = res.status;
      error.statusText = res.statusText;
      error.endpoint = '/user/delete';
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_delete_employee',
        `Delete Employee API error: ${res.status} ${res.statusText} for deleteUser`,
        error,
        {
          endpoint: '/user/delete',
          method: 'DELETE',
          status: res.status,
          statusText: res.statusText,
          employeeId: cleanUserId,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[employeeApiService] Failed to log error:', logError);
      });
      
      throw error;
    }

    return await res.json();
  } catch (error) {
    // Log network/fetch errors to logging service
    if (error.name === 'TypeError' || !error.status) {
      loggingService.logError(
        'api_error_delete_employee',
        `Delete Employee API network/fetch error for deleteUser`,
        error,
        {
          endpoint: '/user/delete',
          method: 'DELETE',
          employeeId: cleanUserId,
          errorType: error?.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[employeeApiService] Failed to log error:', logError);
      });
    }
    throw error;
  }
};



/**
 * Fetch available supervisors
 */
export const fetchAvailableSupervisors = async () => {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required.');

  const res = await fetch(`${baseUrl}/user/supervisors`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  return await res.json();
};

/**
 * Transfer users from one supervisor to another
 */
// employeeApiService.js



export async function transferUsers(fromEmployeeId, toEmployeeId) {
  const url = 'https://api.innoworx.ai/Stage/user/transfer-users';

  const idToken = await getAuthToken();

  if (!idToken) {
    throw new Error("No auth token found. Please log in.");
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      user1_id: fromEmployeeId,
      user2_id: toEmployeeId,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    console.error('Error response:', errorBody);
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return await response.json();
}





// export default {
//   getTenantInfo,
//   fetchRolesByVertical,
//   getUsersByRole,
//   getProfilePhoto,
//   deleteUser,
//   fetchAvailableSupervisors,
//   transferUsers,
// };
